import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  CircularProgress,
  IconButton,
  Paper,
  Theme,
  Tooltip,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import { Link as NavLink } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import {
  DataTable,
  IColDef,
} from 'src/app/modules/Manage/pages/queues-table/DataTable';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { loadableEnum } from '../../common/useMakePHPRequest';
import { trackAddTeam, trackEditViewTeam } from '../../common/userTracking';
import { calendarState } from '../../recoil/atoms/calendarState';
import { user } from '../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { AddAgentModal } from '../components/modals/AddAgentModal';
import { AddTeamModal } from '../components/modals/AddTeamModal';
import { DeleteTeamModal } from '../components/modals/DeleteTeamModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

import { DashboardTourExtraSpotlights } from './dashboardTour/DashboardTourExtraSpotlights';
import { ViewTeam } from './ViewTeam';

const useStyles = makeStyles((theme: Theme & { custom: any }) => ({
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  deleteIconButton: {
    color: theme?.custom?.button?.delete?.main,
  },
  spinner: {
    position: 'fixed !important' as 'fixed',
  },
  actionButton: {
    whiteSpace: 'nowrap',
    flexDirection: 'column',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    '& article': {
      margin: 0,
    },
    '& p': {
      lineHeight: 'normal',
      whiteSpace: 'normal',
      textTransform: 'uppercase',
    },
  },
}));
export function ManageTeams() {
  const [, setCalendarDisplay] = useRecoilStateLoadable(calendarState);
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  // const teams = useRecoilValueLoadable(
  //   makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_TEAMS })
  // );
  const data = {
    call: methodNameEnum.GET_BUSINESS_TEAMS,
  };
  const teams = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_TEAMS,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const teamsCached = useRef([]);
  teamsCached.current = useMemo(
    () =>
      teams.contents?.response?.data?.result
        ? teams.contents?.response?.data?.result?.map((o, index) => ({
            ...o,
            id: o.teamid,
          }))
        : teamsCached.current,
    [teams]
  );
  const [teamName, setTeamName] = useState('');
  const [id, setId] = useState(0);

  // actions for opening and closing modal for add-agents
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // for add teams
  const [showTeam, setShowTeam] = useState(false);
  const handleCloseTeam = () => setShowTeam(false);
  const handleShowTeam = () => setShowTeam(true);
  // delete modal handlers
  const [showDeleteTeam, setShowDeleteTeam] = useState(false);
  const handleCloseDeleteTeam = () => setShowDeleteTeam(false);
  const handleDeleteTeam = () => setShowDeleteTeam(true);
  const [deleteStatus, _setDeleteStatus] = useState(loadableEnum.EMPTY);
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [deleteGroupId, setDeleteGroupId] = useState<string | undefined>('');
  const setRefetchToggleForBusinessTeams = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_TEAMS)
  );
  useEffect(
    () => {
      setCalendarDisplay('none');
    },
    // listening for change in teams to rerender data -line:33
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teams]
  );
  // const [, setViewProps] = useRecoilStateLoadable(teamViewProps);
  function addAction({ teamid, teamname }: GridRowData) {
    setTeamName(teamname);
    setId(teamid);
    handleShow();
  }

  // const makeRequest = useMakePHPRequest({
  //   userAtom: userAtom.contents,
  //   setStatus: setDeleteStatus,
  // });
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  function formatter(params: GridRenderCellParams) {
    return deleteGroupId === params.row.teamid ? (
      <div className={classes.actionButton}>
        <article>
          <IconButton style={{ marginLeft: '20px' }} size="large"></IconButton>
          <Tooltip title="Confirm Deletion">
            <IconButton
              aria-label="accept"
              onClick={async (event) => {
                event.preventDefault(); // stop link
                // insert action to delete here and refetch
                // await makeRequest({
                //   methodName: methodNameEnum.DELETE_TEAM,
                //   payload: {
                // teamid: params.row.teamid,
                //   },
                // });
                const data = {
                  call: methodNameEnum.DELETE_TEAM,
                  apikey: userAtom.contents.apikey,
                  businessid: userAtom.contents.businessid,
                  teamid: params.row.teamid,
                };
                const response = await mutation.mutateAsync({
                  endpoint: endpointEnum.DELETE_TEAM,
                  payload: data,
                });
                setRefetchToggleForBusinessTeams({});
                if (response?.data?.success) {
                  enqueueSnackbarWithStyles({
                    message: `Successfully Deleted ${params.row.teamname} Team`,
                    alertProps: { severity: 'success' },
                  });
                } else {
                  enqueueSnackbarWithStyles({
                    message: `Encountered an error deleting the ${params.row.teamname} Team`,
                    alertProps: { severity: 'warning' },
                  });
                }
              }}
              disabled={
                mutation.isLoading || deleteStatus === loadableEnum.LOADING
              }
              size="large"
            >
              <CheckIcon
                color={
                  mutation.isLoading || deleteStatus === loadableEnum.LOADING
                    ? 'disabled'
                    : 'primary'
                }
              />
              {mutation.isLoading ||
                (deleteStatus === loadableEnum.LOADING && (
                  <CircularProgress
                    size={43}
                    className={classes.buttonProgress}
                  />
                ))}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton
              aria-label="cancel"
              onClick={(event) => {
                event.preventDefault(); // stop link
                // reset groupid hide confirm / cancel deletion
                setDeleteGroupId(undefined);
              }}
              disabled={
                mutation.isLoading || deleteStatus === loadableEnum.LOADING
              }
              size="large"
            >
              <ClearIcon
                color="disabled"
                className={clsx({
                  [classes.deleteIconButton]:
                    !mutation.isLoading &&
                    deleteStatus !== loadableEnum.LOADING, // || deleteCancelTokenSource,
                })}
              />
            </IconButton>
          </Tooltip>
        </article>
      </div>
    ) : (
      <div>
        <Tooltip title="Add Member To Team">
          <IconButton
            aria-label="add"
            onClick={() => {
              addAction(params.row);
            }}
            size="large"
          >
            <PersonAddIcon style={{ cursor: 'pointer' }} color="primary" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit/View Team">
          <NavLink
            // className="btn btn-icon btn-light btn-hover-danger btn-sm"
            to={`/manage/teams/${params.row.teamid}/edit`}
            // onClick={() => viewAction(params.row)}
            onClick={() => {
              trackEditViewTeam();
              openTeamsModal(params.row);
            }}
          >
            <IconButton
              // onClick={() => changeAction(params.row)}
              name="edit"
              size="large"
            >
              <CreateIcon color="primary" />
            </IconButton>
          </NavLink>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            aria-label="delete"
            onClick={() => {
              setDeleteGroupId(params.row.teamid);
            }}
            size="large"
          >
            <DeleteIcon className={classes.deleteIconButton} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }
  const [teamsModal, handleTeamsModal] = useState(false);
  const [rowForEdit, setRowForEdit] = useState('');
  const openTeamsModal = (row) => {
    handleTeamsModal(true);
    setRowForEdit(row);
  };
  const teamNameFormatter = useCallback(
    (params) => {
      return (
        <Tooltip title="Edit/View Team">
          <NavLink
            to="#"
            onClick={() => {
              trackEditViewTeam();
              openTeamsModal(params.row);
            }}
          >
            {params.row.teamname}
          </NavLink>
        </Tooltip>
      );
    },
    [openTeamsModal]
  );
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns = [
    {
      field: 'teamname',
      headerName: 'Team Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: teamNameFormatter,
    },
    {
      field: 'memberCount',
      headerName: 'Member Count',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'teamid',
      headerName: 'Actions',
      disableExport: true,
      disableSearch: true,
      enableSorting: false,
      headerAlign: 'center',
      align: 'center',
      width: 206,
      renderCell: formatter,
      hide: readOnly,
    },
  ] /* satisfies */ as IColDef[];
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Teams"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddTeam();
              handleShowTeam();
            }}
            onClickForDelete={handleDeleteTeam}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    [handleShowTeam, handleDeleteTeam, selected.length, readOnly]
  );
  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'teamname', sort: 'asc' },
  ]);
  return (
    <>
      <DashboardTourExtraSpotlights />
      <Paper square={true} elevation={0}>
        <ViewTeam
          show={!!teamsModal}
          setRefetchToggleForBusinessTeams={setRefetchToggleForBusinessTeams}
          onHide={handleTeamsModal}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          id={rowForEdit.id}
        />
        <DataTable
          enableSelectAll={true}
          rows={teamsCached?.current ?? []}
          setSelected={setSelected}
          loading={teams.state === 'loading'}
          columns={columns}
          ToolbarComponent={ToolbarComponent}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
          selected={selected}
        />

        <AddTeamModal
          show={showTeam}
          handleClose={handleCloseTeam}
          setRefetchToggleForBusinessTeams={setRefetchToggleForBusinessTeams}
          // makeRequest={makeRequest}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        />

        <AddAgentModal
          handleClose={handleClose}
          show={show}
          teamName={teamName}
          id={id}
          teams={teamsCached.current}
          userAtom={userAtom}
          setUserAtom={setUserAtom}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          viewTeam={false}
        />

        <DeleteTeamModal
          show={showDeleteTeam}
          handleClose={handleCloseDeleteTeam}
          selected={selected}
          // mutation={mutation}
          userAtom={userAtom}
          setUserAtom={setUserAtom}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          setRefetchToggleForBusinessTeams={setRefetchToggleForBusinessTeams}
        />
      </Paper>
    </>
  );
}
